import React from 'react';
import { CheckCircle2, Eye, PenLine, Pencil, Upload, RefreshCw } from 'lucide-react';
import { firmatoDoc, mancantiDoc } from './firme/useFirme';
import { BadgeStato, quando } from './stati';
import { StoricoDocumento } from './StoricoDocumento';

const Bottone = ({ onClick, icona: Icona, testo, testid, primario }) => (
  <button onClick={onClick} data-testid={testid}
    className={`h-9 px-3 rounded-lg text-xs font-medium flex items-center gap-1 ${primario ? 'bg-gray-900 text-white' : 'border border-gray-300 bg-white text-gray-700'}`}>
    <Icona className="h-3.5 w-3.5" />{testo}
  </button>
);

export const BadgeFirmato = ({ doc, testid }) => (!firmatoDoc(doc) ? null : (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold" data-testid={`${testid}-firmato`}>
    <CheckCircle2 className="h-3 w-3" />Firmato
  </span>
));

export const BottoneFirma = ({ doc, onFirma, onAggiungiFirma, titolo, testid }) => {
  if (mancantiDoc(doc)) {
    return (
      <button onClick={() => onFirma(doc.id)} data-testid={`${testid}-firma`}
        className="h-9 px-3 rounded-lg text-xs font-semibold flex items-center gap-1 bg-amber-500 text-white"><PenLine className="h-3.5 w-3.5" />Firma ({mancantiDoc(doc)})</button>
    );
  }
  return doc?.file && onAggiungiFirma && !firmatoDoc(doc)
    ? <Bottone onClick={() => onAggiungiFirma(doc, titolo)} icona={PenLine} testo="Aggiungi firma" testid={`${testid}-aggiungi-firma`} /> : null;
};

const Azioni = ({ doc, titolo, testid, onCarica, onVedi, onModifica, onFirma, onAggiungiFirma }) => (
  <div className="flex flex-wrap gap-1.5">
    <BottoneFirma doc={doc} onFirma={onFirma} onAggiungiFirma={onAggiungiFirma} titolo={titolo} testid={testid} />
    {doc?.file && <Bottone onClick={() => onVedi(doc, titolo)} icona={Eye} testo="Vedi" testid={`${testid}-vedi`} />}
    {doc && <Bottone onClick={() => onModifica(doc, titolo)} icona={Pencil} testo="Modifica" testid={`${testid}-modifica`} />}
    <Bottone onClick={onCarica} icona={doc ? RefreshCw : Upload} testo={doc ? 'Rinnova' : 'Carica'} testid={`${testid}-carica`} primario={!doc} />
  </div>
);

const Persone = ({ v, onCarica, onVedi, onModifica, onFirma, onAggiungiFirma }) => {
  // 'almeno uno' (RSPP, primo soccorso, antincendio): conta chi ce l'ha, non serve a tutti
  const righe = v.per_persona === 'almeno_uno' ? v.persone.filter((r) => r.documento) : v.persone;
  return (
  <div className="space-y-1.5">
    {!v.persone.length && <p className="text-xs text-gray-500">Aggiungi le persone che lavorano qui (pulsante Persone).</p>}
    {v.persone.length > 0 && !righe.length && <p className="text-xs text-gray-600" data-testid={`documenti-voce-${v.codice}-basta-uno`}>Basta almeno una persona formata: carica il suo attestato.</p>}
    {righe.map((r) => {
      const testid = `documenti-voce-${v.codice}-${r.persona.id.replace(/[^a-zA-Z0-9-]/g, '-')}`;
      return (
        <div key={r.persona.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2" data-testid={testid}>
          <div className="min-w-0">
            <p className="text-sm text-gray-900 font-medium">{r.persona.nome}</p>
            <div className="flex flex-wrap items-center gap-2 mt-0.5"><BadgeStato stato={r.stato} testid={`${testid}-stato`} /><BadgeFirmato doc={r.documento} testid={testid} /><span className="text-[11px] text-gray-500">{quando(r.documento, r.giorni)}</span></div>
          </div>
          <Azioni doc={r.documento} titolo={`${v.nome} - ${r.persona.nome}`} testid={testid}
            onCarica={() => onCarica({ tipo: v.codice, persona_id: r.persona.id, titolo: `${v.nome}: ${r.persona.nome}` })} onVedi={onVedi} onModifica={onModifica} onFirma={onFirma} onAggiungiFirma={onAggiungiFirma} />
        </div>
      );
    })}
  </div>
  );
};

export const VoceDocumento = ({ v, livelli, onCarica, onVedi, onModifica, onNonApplicabile, onFirma, onAggiungiFirma, onEliminato, onDpi }) => {
  const testid = `documenti-voce-${v.codice}`;
  return (
    <div className={`rounded-2xl border p-3.5 space-y-2 bg-white ${['scaduto', 'mancante'].includes(v.stato) ? 'border-red-300' : v.stato === 'in_scadenza' ? 'border-amber-300' : 'border-gray-200'}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{v.nome}</p>
          <p className="text-[11px] text-gray-500">{livelli[v.livello]}{v.norma ? ` · ${v.norma}` : ''}</p>
        </div>
        <BadgeStato stato={v.stato} testid={`${testid}-stato`} />
      </div>
      <p className="text-xs text-gray-600">{v.descrizione}</p>
      {v.codice === 'consegna_dpi' && onDpi && v.stato !== 'non_applicabile' && <button onClick={onDpi} className="h-9 px-3 rounded-lg bg-orange-500 text-white text-xs font-semibold" data-testid={`${testid}-prepara`}>Prepara i verbali dal DVR</button>}
      {v.per_persona ? <Persone v={v} onCarica={onCarica} onVedi={onVedi} onModifica={onModifica} onFirma={onFirma} onAggiungiFirma={onAggiungiFirma} /> : (
        v.stato !== 'non_applicabile' && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-gray-700 flex flex-wrap items-center gap-2" data-testid={`${testid}-quando`}>{v.documento ? `${quando(v.documento, v.giorni)}${v.documento.ente ? ` · ${v.documento.ente}` : ''}` : ''}<BadgeFirmato doc={v.documento} testid={testid} /></span>
            <Azioni doc={v.documento} titolo={v.nome} testid={testid} onCarica={() => onCarica({ tipo: v.codice, titolo: v.nome })} onVedi={onVedi} onModifica={onModifica} onFirma={onFirma} onAggiungiFirma={onAggiungiFirma} />
          </div>
        )
      )}
      <StoricoDocumento v={v} onVedi={onVedi} onEliminato={onEliminato} />
      {v.per_persona && <button onClick={() => onCarica({ tipo: v.codice, titolo: v.nome })} className="text-xs text-sky-700 font-medium" data-testid={`${testid}-carica-altro`}>+ Carica per un'altra persona</button>}
      {v.livello !== 'obbligatorio' && !v.documento && (
        <button onClick={() => onNonApplicabile(v.codice, !v.non_applicabile)} className="text-xs text-gray-500 underline" data-testid={`${testid}-non-applicabile`}>
          {v.non_applicabile ? 'Mi riguarda' : 'Non mi riguarda'}
        </button>
      )}
    </div>
  );
};
